// ── Per-creature authored content (Round 17 Phase F) ──
//
// Source of truth: docs/design/softened-creatures.md §1–§2.
// Each entry maps an EnemyType archetype to its authored identity:
//   • v2 name (the poetic name used in logs and UI)
//   • short-log tag (for the unburdening log composite format, §4)
//   • behavior tone (one-line summary for UI/tooltip context)
//   • sprite key (client asset key; placeholder until art batch lands)
//   • home realms with spawn weights (from §1.x per-realm tendencies)
//   • unburdening flavor pool (3–5 authored variants per §1.x)
//
// The 6 creatures selected for Phase F are the core Expedition-interior
// archetypes. Guardians (§1.8) and the two regional fauna (§2) are
// handled in their own rounds (18 and later).
import { EnemyType } from '../types/entities.js';
import { RealmId } from './realms.js';
// ── The 6 authored creatures ────────────────────────────────────────────
export const CREATURE_CONTENT = [
    // §1.1 — Verdana core
    {
        enemyType: EnemyType.OneWhoForgotToStop,
        name: 'One Who Forgot To Stop',
        shortLogTag: 'a forgetful guard',
        behaviorTone: 'A guard whose gate no longer exists, still walking a patrol no one asked them to finish.',
        spriteKey: 'creature_placeholder_forgot_to_stop',
        realmWeights: [
            { realmId: RealmId.VerdanaFields, weight: 0.45 },
            { realmId: RealmId.SablewindReaches, weight: 0.15 },
            { realmId: RealmId.FrostmereExpanse, weight: 0.10 },
            { realmId: RealmId.EmberMire, weight: 0.10 },
        ],
        unburdeningFlavor: [
            'They stopped walking. Then they stopped.',
            'The patrol was finally over.',
            'They sat down on the grass and did not get up.',
            'Their shift ended, late, but ended.',
            'Somebody, long ago, had told them they could go home.',
        ],
    },
    // §1.2 — Sablewind core
    {
        enemyType: EnemyType.OneWhoKeptAiming,
        name: 'One Who Kept Aiming',
        shortLogTag: 'a patient archer',
        behaviorTone: 'An archer whose arrow never arrived, still holding the draw after decades.',
        spriteKey: 'creature_placeholder_kept_aiming',
        realmWeights: [
            { realmId: RealmId.SablewindReaches, weight: 0.35 },
            { realmId: RealmId.VerdanaFields, weight: 0.25 },
            { realmId: RealmId.FrostmereExpanse, weight: 0.15 },
            { realmId: RealmId.EmberMire, weight: 0.10 },
        ],
        unburdeningFlavor: [
            'They set the bow down, gently, and watched the arrow drift into the grass.',
            'The draw was finally released. The forest sighed.',
            'They remembered the name of who they had been aiming for, and let the name go too.',
            'The string grew quiet.',
            'Some arrows are meant not to land.',
        ],
    },
    // §1.3 — Frostmere core
    {
        enemyType: EnemyType.OneWhoHeldTheLine,
        name: 'One Who Held The Line',
        shortLogTag: 'a patient shield',
        behaviorTone: 'A shield-bearer whose promise outlasted every person it was made to.',
        spriteKey: 'creature_placeholder_held_the_line',
        realmWeights: [
            { realmId: RealmId.FrostmereExpanse, weight: 0.25 },
            { realmId: RealmId.VerdanaFields, weight: 0.15 },
            { realmId: RealmId.SablewindReaches, weight: 0.05 },
            { realmId: RealmId.EmberMire, weight: 0.10 },
        ],
        unburdeningFlavor: [
            'The shield lowered. They leaned against it, and the weight was finally shared.',
            'The line held long enough. Someone had come at last.',
            'They set the shield down. Nothing came through.',
            'The promise was kept. They were allowed to sit.',
            'The battle had been over for a very long time.',
        ],
    },
    // §1.4 — Frostmere secondary
    {
        enemyType: EnemyType.OneWhoCouldNotStandStill,
        name: 'One Who Could Not Stand Still',
        shortLogTag: 'a restless visitor',
        behaviorTone: 'Grief moves them from place to place; they blink not to escape, but because standing still is unbearable.',
        spriteKey: 'creature_placeholder_could_not_stand_still',
        realmWeights: [
            { realmId: RealmId.FrostmereExpanse, weight: 0.30 },
            { realmId: RealmId.SablewindReaches, weight: 0.15 },
            { realmId: RealmId.EmberMire, weight: 0.15 },
        ],
        unburdeningFlavor: [
            'They stopped somewhere and did not leave.',
            'The place, finally, was alright to be in.',
            'Their grief sat down beside them instead of chasing them.',
            'They stayed.',
            'One step more would have been too many. They did not take it.',
        ],
    },
    // §1.5 — Sablewind secondary
    {
        enemyType: EnemyType.OneWhoDissolved,
        name: 'One Who Dissolved',
        shortLogTag: 'a fading presence',
        behaviorTone: 'They did not mean to disappear; absence became a habit, and the Softening finished what loneliness started.',
        spriteKey: 'creature_placeholder_dissolved',
        realmWeights: [
            { realmId: RealmId.SablewindReaches, weight: 0.20 },
            { realmId: RealmId.VerdanaFields, weight: 0.05 },
            { realmId: RealmId.EmberMire, weight: 0.05 },
        ],
        unburdeningFlavor: [
            'They drifted the rest of the way, and were gone.',
            'The room they were remembered in grew a little warmer.',
            'Someone, somewhere, finally looked up.',
            'The wind that held them gave up, and they drifted away.',
            'They became the quiet at the edge of a room instead of the absence.',
        ],
    },
    // §1.7 — Ember Mire core
    {
        enemyType: EnemyType.OneWhoBrieflyRemembered,
        name: 'One Who Briefly Remembered',
        shortLogTag: 'a sudden strength',
        behaviorTone: 'Mostly tired, but sometimes something cuts through and they remember being strong — then the memory fades and they grieve.',
        spriteKey: 'creature_placeholder_briefly_remembered',
        realmWeights: [
            { realmId: RealmId.EmberMire, weight: 0.30 },
            { realmId: RealmId.SablewindReaches, weight: 0.10 },
            { realmId: RealmId.FrostmereExpanse, weight: 0.05 },
            { realmId: RealmId.VerdanaFields, weight: 0.10 },
        ],
        unburdeningFlavor: [
            'They remembered who they were for one bright moment, and then they let it go.',
            'The strength left them, and they were grateful.',
            'For a second they were thirty again. Then they were themselves again.',
            'They did not mean to flash so brightly. They apologized, and rested.',
            'The memory passed, like a wind, and they sat down.',
        ],
    },
];
// ── Derived indexes ─────────────────────────────────────────────────────
/** O(1) lookup by EnemyType. */
export const CREATURE_CONTENT_BY_TYPE = new Map(CREATURE_CONTENT.map((c) => [c.enemyType, c]));
/** Creature types that appear in a given realm (weight > 0), sorted by weight descending. */
export const CREATURES_BY_REALM = {
    [RealmId.VerdanaFields]: CREATURE_CONTENT
        .filter((c) => c.realmWeights.some((w) => w.realmId === RealmId.VerdanaFields && w.weight > 0))
        .sort((a, b) => {
        const wa = a.realmWeights.find((w) => w.realmId === RealmId.VerdanaFields)?.weight ?? 0;
        const wb = b.realmWeights.find((w) => w.realmId === RealmId.VerdanaFields)?.weight ?? 0;
        return wb - wa;
    }),
    [RealmId.SablewindReaches]: CREATURE_CONTENT
        .filter((c) => c.realmWeights.some((w) => w.realmId === RealmId.SablewindReaches && w.weight > 0))
        .sort((a, b) => {
        const wa = a.realmWeights.find((w) => w.realmId === RealmId.SablewindReaches)?.weight ?? 0;
        const wb = b.realmWeights.find((w) => w.realmId === RealmId.SablewindReaches)?.weight ?? 0;
        return wb - wa;
    }),
    [RealmId.FrostmereExpanse]: CREATURE_CONTENT
        .filter((c) => c.realmWeights.some((w) => w.realmId === RealmId.FrostmereExpanse && w.weight > 0))
        .sort((a, b) => {
        const wa = a.realmWeights.find((w) => w.realmId === RealmId.FrostmereExpanse)?.weight ?? 0;
        const wb = b.realmWeights.find((w) => w.realmId === RealmId.FrostmereExpanse)?.weight ?? 0;
        return wb - wa;
    }),
    [RealmId.EmberMire]: CREATURE_CONTENT
        .filter((c) => c.realmWeights.some((w) => w.realmId === RealmId.EmberMire && w.weight > 0))
        .sort((a, b) => {
        const wa = a.realmWeights.find((w) => w.realmId === RealmId.EmberMire)?.weight ?? 0;
        const wb = b.realmWeights.find((w) => w.realmId === RealmId.EmberMire)?.weight ?? 0;
        return wb - wa;
    }),
};
//# sourceMappingURL=creatureContent.js.map